// @oasis-permission-inherits: /linkedin-skills (linkedin-skills:manage)
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  deleteLinkedInCredibilitySignalMutation,
  removeLinkedInCredentialsMutation,
  upsertLinkedInCredibilitySignalMutation,
} from "~/modules/linkedin-skills/mutations";
import {
  linkedInCredibilitySignalQuery,
  linkedInCredentialsQuery,
  linkedInProfileQuery,
  linkedInSkillLevelsQuery,
  linkedInSkillsQuery,
} from "~/modules/linkedin-skills/queries";
import { lookupUserByIdQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/linkedin-skills/users/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    if (!params.userId) {
      throw new Error("User ID is required");
    }

    context.queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({ pathParams: { userId: params.userId } })
    );
  },
});

function RouteComponent() {
  const { userId } = Route.useParams();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteSignalConfirm, setShowDeleteSignalConfirm] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<string | undefined>(undefined);
  const [shouldFetchProfile, setShouldFetchProfile] = useState(false);

  useEffect(() => {
    setSelectedSkill(undefined);
    setShouldFetchProfile(false);
  }, [userId]);

  const { data: user } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  const { data: credentials, isLoading } = linkedInCredentialsQuery.useQuery({
    pathParams: { userId },
  });

  const {
    data: skills,
    isLoading: isSkillsLoading,
    isError: isSkillsError,
  } = linkedInSkillsQuery.useQuery(
    { pathParams: { userId } },
    { enabled: credentials?.connected === true }
  );

  const { data: skillLevels, isLoading: isSkillLevelsLoading } = linkedInSkillLevelsQuery.useQuery({
    enabled: credentials?.connected === true,
  });

  const { data: credibilitySignal, isLoading: isCredibilitySignalLoading } =
    linkedInCredibilitySignalQuery.useQuery(
      { pathParams: { userId } },
      { enabled: credentials?.connected === true }
    );

  const {
    data: linkedInProfile,
    isFetching: isProfileFetching,
    isError: isProfileError,
    refetch: refetchProfile,
  } = linkedInProfileQuery.useQuery(
    shouldFetchProfile && credentials?.connected === true ? { pathParams: { userId } } : skipToken
  );

  const { mutate: upsertSignal, isPending: isUpsertPending } =
    upsertLinkedInCredibilitySignalMutation.useMutation({
      onSuccess() {
        toast.success("Skill updated");
      },
      onError() {
        toast.error("Failed to update skill");
      },
    });

  const { mutate: deleteSignal, isPending: isDeleteSignalPending } =
    deleteLinkedInCredibilitySignalMutation.useMutation({
      onSuccess() {
        setShowDeleteSignalConfirm(false);
        toast.success("Skill removed from LinkedIn");
      },
      onError() {
        setShowDeleteSignalConfirm(false);
        toast.error("Failed to remove skill");
      },
    });

  const { mutate, isPending } = removeLinkedInCredentialsMutation.useMutation({
    onSuccess() {
      setShowConfirm(false);
      toast.success("LinkedIn credentials removed");
    },
    onError() {
      setShowConfirm(false);
      toast.error("Failed to remove LinkedIn credentials");
    },
  });

  const userLabel = user?.user?.name ?? user?.user?.email ?? userId;

  function renderSkills() {
    if (isSkillsLoading) {
      return (
        <div className="space-y-4">
          <h2 className="text-base font-semibold">LinkedIn Skills</h2>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      );
    }
    if (isSkillsError) {
      return (
        <div className="space-y-4">
          <h2 className="text-base font-semibold">LinkedIn Skills</h2>
          <div className="rounded-md border bg-muted/20 p-4">
            <p className="text-sm text-muted-foreground">Failed to load skills data.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <h2 className="text-base font-semibold">LinkedIn Skills</h2>
        <div className="rounded-md border p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Videos Created</p>
              <p className="text-sm font-medium">{skills?.videosCreatedCount ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Videos Exported</p>
              <p className="text-sm font-medium">{skills?.videosExportedCount ?? "—"}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Skill Level</p>
            <p className="text-sm font-medium">
              {skills?.skillLabel ?? "—"}
              {skills?.skill && (
                <span className="ml-2 text-xs text-muted-foreground">({skills.skill})</span>
              )}
            </p>
          </div>
          {skills?.metricsUpdatedAt && (
            <p className="text-xs text-muted-foreground">
              Updated: {new Date(skills.metricsUpdatedAt).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    );
  }

  function renderManageSkill() {
    const effectiveSkill = selectedSkill ?? credibilitySignal?.skill ?? undefined;
    return (
      <div className="space-y-4">
        <h2 className="text-base font-semibold">Manage Skill on LinkedIn Platform</h2>
        <div className="flex items-center gap-3">
          <Select
            value={effectiveSkill}
            onValueChange={setSelectedSkill}
            disabled={isSkillLevelsLoading}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select a skill level" />
            </SelectTrigger>
            <SelectContent>
              {skillLevels?.levels.map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              if (!effectiveSkill) {
                return;
              }
              upsertSignal({ params: { skill: effectiveSkill }, pathParams: { userId } });
            }}
            disabled={
              isSkillLevelsLoading ||
              isCredibilitySignalLoading ||
              isUpsertPending ||
              !effectiveSkill
            }
          >
            {isUpsertPending ? "Updating..." : "Update Skill"}
          </Button>
          {credibilitySignal?.skill != null && (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteSignalConfirm(true)}
              disabled={isDeleteSignalPending}
            >
              Remove from LinkedIn
            </Button>
          )}
        </div>
      </div>
    );
  }

  function renderLinkedInProfile() {
    return (
      <div className="space-y-3">
        <h2 className="text-base font-semibold">LinkedIn Profile</h2>
        <div className="flex flex-col items-start gap-2">
          <Button
            onClick={() => {
              if (shouldFetchProfile) {
                refetchProfile();
              } else {
                setShouldFetchProfile(true);
              }
            }}
            disabled={isProfileFetching}
          >
            {isProfileFetching ? "Fetching..." : "Fetch LinkedIn profile"}
          </Button>
          {shouldFetchProfile && !isProfileFetching && isProfileError && (
            <p className="text-sm text-destructive">Could not load profile</p>
          )}
          {shouldFetchProfile && !isProfileFetching && !isProfileError && linkedInProfile && (
            <div className="rounded-md border p-4 space-y-1">
              <p className="text-sm font-medium">
                {linkedInProfile.firstName} {linkedInProfile.lastName}
              </p>
              <p className="text-xs text-muted-foreground">LinkedIn ID: {linkedInProfile.id}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderCredentials() {
    if (isLoading) {
      return <p className="text-sm text-muted-foreground">Loading...</p>;
    }
    if (!credentials) {
      return (
        <div className="rounded-md border bg-muted/20 p-4">
          <p className="text-sm text-muted-foreground">Failed to load credential information.</p>
        </div>
      );
    }
    if (!credentials.connected) {
      return (
        <div className="rounded-md border bg-muted/20 p-4">
          <p className="text-sm text-muted-foreground">No LinkedIn credentials on file.</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div className="rounded-md border p-4 space-y-2">
          <p className="text-sm font-medium">Status: Connected</p>
          {credentials.expiresAt && (
            <p className="text-sm text-muted-foreground">
              Expires: {new Date(credentials.expiresAt).toLocaleString()}
            </p>
          )}
          {credentials.scopes && credentials.scopes.length > 0 && (
            <p className="text-sm text-muted-foreground">Scopes: {credentials.scopes.join(", ")}</p>
          )}
        </div>
        <Button variant="destructive" onClick={() => setShowConfirm(true)}>
          Remove credentials
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/users/view/$userId"
          params={{ userId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to User
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-bold">LinkedIn Credentials</h1>
        <p className="text-sm text-muted-foreground mt-1">{userLabel}</p>
      </div>

      {renderCredentials()}

      {credentials?.connected && renderLinkedInProfile()}

      {credentials?.connected && renderSkills()}

      {credentials?.connected && renderManageSkill()}

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove LinkedIn Credentials</DialogTitle>
            <DialogDescription>
              This will remove the LinkedIn credentials for{" "}
              <span className="font-semibold">{userLabel}</span>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => mutate({ params: {}, pathParams: { userId } })}
              disabled={isPending}
            >
              {isPending ? "Removing..." : "Remove credentials"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteSignalConfirm} onOpenChange={setShowDeleteSignalConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove LinkedIn Skill</DialogTitle>
            <DialogDescription>
              This will remove the credibility signal from the user&apos;s LinkedIn profile. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteSignalConfirm(false)}
              disabled={isDeleteSignalPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteSignal({ params: {}, pathParams: { userId } })}
              disabled={isDeleteSignalPending}
            >
              {isDeleteSignalPending ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
